import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, User, CheckCircle } from "lucide-react";
import { useAppSelector } from "hooks/hooks";
import { getAuthToken } from "utils/storage";

interface ChatStep {
  id: string;
  type: "question" | "response";
  content: string;
  completed?: boolean;
}

interface UserPreferences {
  eventType?: string;
  guestCount?: string;
  budget?: string;
  name?: string;
  email?: string;
  phone?: string;
  message?: string;
}

const EventToBeeChatbot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [userPreferences, setUserPreferences] = useState<UserPreferences>({});
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [adminMessages, setAdminMessages] = useState<any[]>([]);
  const [hasNewMessage, setHasNewMessage] = useState(false);
  const { user } = useAppSelector((state) => state.auth);

  const chatSteps: ChatStep[] = [
    {
      id: "welcome",
      type: "question",
      content: `Bonjour ! Je suis l'assistant EventToBee 👋\n\nJe peux vous aider à trouver l'espace parfait pour votre événement ou vous mettre en relation avec notre équipe.\n\nQuel type d'événement organisez-vous ?`,
    },
    {
      id: "eventType",
      type: "response",
      content: "Parfait ! Combien d'invités attendez-vous approximativement ?",
    },
    {
      id: "guestCount",
      type: "response",
      content: "Excellent ! Quel est votre budget approximatif pour la location ?",
    },
    {
      id: "budget",
      type: "response",
      content: "Merci pour ces informations ! Pour mieux vous aider, pourriez-vous me donner votre nom ?",
    },
    {
      id: "name",
      type: "response",
      content: "Et votre adresse email pour que nous puissions vous recontacter ?",
    },
    {
      id: "email",
      type: "response",
      content: "Parfait ! Avez-vous un numéro de téléphone ? (optionnel)",
    },
    {
      id: "phone",
      type: "response",
      content: "Enfin, avez-vous des besoins spécifiques ou des questions particulières ?",
    },
    {
      id: "message",
      type: "response",
      content:
        "Merci beaucoup ! Je transmets vos informations à notre équipe qui vous recontactera très rapidement. 🎉",
    },
  ];

  const handleSubmit = async (value: string) => {
    console.log("🤖 handleSubmit called with:", { value, currentStep, chatStepsLength: chatSteps.length });

    if (!value.trim() && currentStep !== chatSteps.length - 2) return;

    setIsLoading(true);

    // Store user response
    const newPreferences = { ...userPreferences };

    switch (currentStep) {
      case 0:
        newPreferences.eventType = value;
        break;
      case 1:
        newPreferences.guestCount = value;
        break;
      case 2:
        newPreferences.budget = value;
        break;
      case 3:
        newPreferences.name = value;
        break;
      case 4:
        newPreferences.email = value;
        break;
      case 5:
        newPreferences.phone = value;
        break;
      case 6:
        newPreferences.message = value;
        break;
    }

    setUserPreferences(newPreferences);
    setInputValue("");

    // If this is the last step, send to admin
    if (currentStep === chatSteps.length - 2) {
      sendToAdmin(newPreferences)
        .then(() => {
          setIsCompleted(true);
        })
        .catch((error) => {
          console.error("Error sending to admin:", error);
          setIsCompleted(true);
        });
    }

    // Move to next step
    setTimeout(() => {
      setCurrentStep((prev) => Math.min(prev + 1, chatSteps.length - 1));
      setIsLoading(false);
    }, 1000);
  };

  const sendToAdmin = async (preferences: UserPreferences) => {
    const token = getAuthToken();
    console.log("SendToAdmin called:", { user, token: !!token, preferences });

    if (!user) {
      console.warn("No user logged in, cannot send to admin");
      setIsCompleted(true);
      return;
    }

    if (!token) {
      console.warn("No auth token found");
      setIsCompleted(true);
      return;
    }

    try {
      console.log("Sending to admin with data:", {
        message: `Nouvelle demande via le chatbot EventToBee`,
        userPreferences: preferences,
      });

      const response = await fetch("/api/chatbot/initiate-admin-chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: `Nouvelle demande via le chatbot EventToBee`,
          userPreferences: preferences,
        }),
      });

      console.log("Response status:", response.status);

      if (!response.ok) {
        const errorData = await response.text();
        console.error("Response error:", errorData);
        throw new Error(`Failed to send message to admin: ${response.status} ${errorData}`);
      }

      const data = await response.json();
      console.log("Message sent to admin successfully:", data);

      // Store the conversation ID to check for responses
      if (data.conversationId) {
        setActiveConversationId(data.conversationId);
        // Start checking for admin responses
        checkForAdminResponses(data.conversationId);
      }
    } catch (error) {
      console.error("Error sending to admin:", error);
      throw error;
    }
  };

  const checkForAdminResponses = async (conversationId: string) => {
    const token = getAuthToken();
    if (!token || !user) return;

    try {
      const response = await fetch("/api/conversations/", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const conversation = data.conversations?.find((conv: any) => conv.id === conversationId);

        if (conversation && conversation.chats) {
          // Filter messages from admin (not from current user)
          const adminResponses = conversation.chats.filter(
            (chat: any) => chat.sender_id !== user.id && !chat.content.includes("chatbot EventToBee")
          );

          if (adminResponses.length > adminMessages.length) {
            setAdminMessages(adminResponses);
            setHasNewMessage(true);

            // Show notification on chatbot button if closed
            if (!isOpen) {
              setHasNewMessage(true);
            }
          }
        }
      }
    } catch (error) {
      console.error("Error checking for admin responses:", error);
    }
  };

  // Check for admin responses periodically
  useEffect(() => {
    if (activeConversationId && isCompleted) {
      const interval = setInterval(() => {
        checkForAdminResponses(activeConversationId);
      }, 3000); // Check every 3 seconds

      return () => clearInterval(interval);
    }
  }, [activeConversationId, isCompleted, adminMessages.length]);

  // Clear notification when opening chatbot
  useEffect(() => {
    if (isOpen && hasNewMessage) {
      setHasNewMessage(false);
    }
  }, [isOpen]);

  const resetChat = () => {
    setCurrentStep(0);
    setUserPreferences({});
    setInputValue("");
    setIsCompleted(false);
    setActiveConversationId(null);
    setAdminMessages([]);
    setHasNewMessage(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(inputValue);
    }
  };

  const getUserResponse = (step: number): string => {
    switch (step) {
      case 0:
        return userPreferences.eventType || "";
      case 1:
        return userPreferences.guestCount || "";
      case 2:
        return userPreferences.budget || "";
      case 3:
        return userPreferences.name || "";
      case 4:
        return userPreferences.email || "";
      case 5:
        return userPreferences.phone || "Non renseigné";
      case 6:
        return userPreferences.message || "Pas de questions particulières";
      default:
        return "";
    }
  };

  const getInputPlaceholder = (): string => {
    switch (currentStep) {
      case 0:
        return "Ex: Anniversaire, mariage, conférence...";
      case 1:
        return "Ex: 20 personnes, 50-100 personnes...";
      case 2:
        return "Ex: 500€, 1000-2000€...";
      case 3:
        return "Votre nom complet";
      case 4:
        return "votre.email@exemple.com";
      case 5:
        return "06 XX XX XX XX (optionnel)";
      case 6:
        return "Vos questions ou besoins spécifiques...";
      default:
        return "Tapez votre message...";
    }
  };

  return (
    <>
      {/* Floating Chat Button */}
      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="fixed bottom-6 right-6 z-50">
        <div className="relative">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => setIsOpen(true)}
            className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-200"
          >
            <MessageCircle className="w-6 h-6" />
          </motion.button>

          {/* Notification Badge */}
          {hasNewMessage && !isOpen && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold animate-pulse"
            >
              !
            </motion.div>
          )}
        </div>
      </motion.div>

      {/* Chat Widget */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-20 right-4 sm:right-6 z-50 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-[calc(100vw-2rem)] sm:w-80 md:w-96 h-[500px] max-h-[70vh] flex flex-col overflow-hidden border border-gray-200 dark:border-gray-700"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-purple-600 to-pink-600">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-white/20 backdrop-blur-lg rounded-full flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Assistant EventToBee</h3>
                  <p className="text-purple-100 text-xs">En ligne maintenant</p>
                </div>
              </div>

              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setIsOpen(false)}
                className="w-8 h-8 bg-white/20 backdrop-blur-lg rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
              >
                <X className="w-4 h-4" />
              </motion.button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Initial welcome message */}
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-4 h-4 text-white" />
                </div>
                <div className="max-w-xs">
                  <div className="bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-2xl rounded-bl-md px-4 py-3">
                    <p className="whitespace-pre-wrap text-sm">{chatSteps[0].content}</p>
                  </div>

                  {/* Quick suggestions for first step */}
                  {currentStep === 0 && (
                    <div className="mt-3 space-y-2">
                      <p className="text-xs text-gray-600 dark:text-gray-400">Suggestions rapides :</p>
                      <div className="flex flex-wrap gap-2">
                        {[
                          "Mariage",
                          "Anniversaire",
                          "Conférence",
                          "Soirée d'entreprise",
                          "Baby shower",
                          "Parler à un conseiller",
                        ].map((suggestion, index) => (
                          <button
                            key={index}
                            onClick={() => {
                              console.log("🤖 Suggestion clicked:", suggestion);

                              if (suggestion === "Parler à un conseiller") {
                                console.log("🤖 Direct admin contact requested");
                                setIsLoading(true);
                                // Skip to the end and send directly to admin
                                const preferences = {
                                  eventType: "Demande de conseiller",
                                  message:
                                    "L'utilisateur souhaite parler directement à un conseiller sans passer par le questionnaire.",
                                };
                                setUserPreferences(preferences);
                                console.log("🤖 About to call sendToAdmin with preferences:", preferences);
                                sendToAdmin(preferences)
                                  .then(() => {
                                    console.log("🤖 sendToAdmin succeeded, setting completed to true");
                                    setIsCompleted(true);
                                    setIsLoading(false);
                                  })
                                  .catch((error) => {
                                    console.error("🤖 Error in direct admin contact:", error);
                                    setIsCompleted(true);
                                    setIsLoading(false);
                                  });
                              } else {
                                handleSubmit(suggestion);
                              }
                            }}
                            className="px-3 py-1 text-xs bg-purple-100 hover:bg-purple-200 dark:bg-purple-900 dark:hover:bg-purple-800 text-purple-800 dark:text-purple-200 rounded-full transition-colors"
                          >
                            {suggestion}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* User responses and bot questions */}
              {Array.from({ length: currentStep }, (_, index) => (
                <div key={index}>
                  {/* User response */}
                  {index > 0 && (
                    <div className="flex items-start space-x-3 flex-row-reverse space-x-reverse mb-3">
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-white" />
                      </div>
                      <div className="max-w-xs">
                        <div className="bg-blue-500 text-white rounded-2xl rounded-br-md px-4 py-3">
                          <p className="text-sm">{getUserResponse(index - 1)}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Bot question */}
                  {index < chatSteps.length - 1 && (
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <MessageCircle className="w-4 h-4 text-white" />
                      </div>
                      <div className="max-w-xs">
                        <div className="bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-2xl rounded-bl-md px-4 py-3">
                          <p className="text-sm">{chatSteps[index + 1]?.content}</p>
                        </div>

                        {/* Quick suggestions for guest count */}
                        {currentStep === 1 && index === 0 && (
                          <div className="mt-3 space-y-2">
                            <p className="text-xs text-gray-600 dark:text-gray-400">Suggestions :</p>
                            <div className="flex flex-wrap gap-2">
                              {["10-20 personnes", "20-50 personnes", "50-100 personnes", "100+ personnes"].map(
                                (suggestion, i) => (
                                  <button
                                    key={i}
                                    onClick={() => handleSubmit(suggestion)}
                                    className="px-3 py-1 text-xs bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:hover:bg-blue-800 text-blue-800 dark:text-blue-200 rounded-full transition-colors"
                                  >
                                    {suggestion}
                                  </button>
                                )
                              )}
                            </div>
                          </div>
                        )}

                        {/* Quick suggestions for budget */}
                        {currentStep === 2 && index === 1 && (
                          <div className="mt-3 space-y-2">
                            <p className="text-xs text-gray-600 dark:text-gray-400">Suggestions :</p>
                            <div className="flex flex-wrap gap-2">
                              {["200-500€", "500-1000€", "1000-2000€", "2000€+"].map((suggestion, i) => (
                                <button
                                  key={i}
                                  onClick={() => handleSubmit(suggestion)}
                                  className="px-3 py-1 text-xs bg-green-100 hover:bg-green-200 dark:bg-green-900 dark:hover:bg-green-800 text-green-800 dark:text-green-200 rounded-full transition-colors"
                                >
                                  {suggestion}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Loading indicator */}
              {isLoading && (
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-4 h-4 text-white" />
                  </div>
                  <div className="max-w-xs">
                    <div className="bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-2xl rounded-bl-md px-4 py-3">
                      <div className="flex items-center space-x-2">
                        <div className="flex space-x-1">
                          <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce"></div>
                          <div
                            className="w-2 h-2 bg-purple-500 rounded-full animate-bounce"
                            style={{ animationDelay: "0.1s" }}
                          ></div>
                          <div
                            className="w-2 h-2 bg-purple-500 rounded-full animate-bounce"
                            style={{ animationDelay: "0.2s" }}
                          ></div>
                        </div>
                        <span className="text-sm">L'assistant écrit...</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Completion message */}
              {isCompleted && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <p className="text-green-800 dark:text-green-200 font-semibold">Demande envoyée !</p>
                  </div>
                  <p className="text-green-700 dark:text-green-300 text-sm mt-1">
                    {adminMessages.length > 0
                      ? "Notre équipe vous a répondu ci-dessous !"
                      : "Notre équipe vous recontactera très bientôt."}
                  </p>
                  <button
                    onClick={resetChat}
                    className="mt-3 text-sm text-green-600 hover:text-green-800 underline"
                  >
                    Recommencer
                  </button>
                </div>
              )}

              {/* Admin Messages */}
              {adminMessages.length > 0 &&
                adminMessages.map((message, index) => (
                  <motion.div
                    key={`admin-${index}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.3 }}
                    className="flex items-start space-x-3"
                  >
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <div className="max-w-xs">
                      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-900 dark:text-blue-100 rounded-2xl rounded-bl-md px-4 py-3">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">
                            Conseiller EventToBee
                          </span>
                          <span className="text-xs text-blue-500 dark:text-blue-400">
                            {new Date(message.createdAt).toLocaleTimeString("fr-FR", {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        </div>
                        <p className="whitespace-pre-wrap text-sm">{message.content}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
            </div>

            {/* Input */}
            {!isCompleted && currentStep < chatSteps.length - 1 && (
              <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-3">
                  <input
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={getInputPlaceholder()}
                    disabled={isLoading}
                    className="flex-1 px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all disabled:opacity-50"
                  />

                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleSubmit(inputValue)}
                    disabled={!inputValue.trim() || isLoading}
                    className="px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:from-purple-700 hover:to-pink-700 transition-all duration-200"
                  >
                    <Send className="w-4 h-4" />
                  </motion.button>
                </div>

                {!user && currentStep === 0 && (
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    💡 Connectez-vous pour un service plus personnalisé
                  </p>
                )}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default EventToBeeChatbot;
